import { memo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Alert,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from "@mui/material";
import LoadingComponent from "../components/LoadingComponent";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useShippingZoneDetails } from "../hooks/useShippingZoneDetails";
import { useShippingZoneOptions } from "../hooks/useShippingZoneOptions";
import EditShippingZoneModal from "../components/modals/ShippingZone/EditShippingZoneModal";
import WarehouseAssignmentModal from "../components/modals/ShippingZone/WarehouseAssignmentModal";
import { useRegions } from "../hooks/useRegions";
import { ShippingZoneCommunes } from "../components/ShippingZoneCommunes";

const ShippingZoneDetail = () => {
  const {
    zone,
    assignments,
    loading,
    error,
    refetchZone,
    updateZone,
    createShippingZoneAssignment,
    updateShippingZoneAssignment,
    deleteShippingZoneAssignment,
  } = useShippingZoneDetails();
  const {
    loadingOptions,
    warehouseOptions,
    communeOptions,
    courierOptions,
    adminOptions,
    methodOptions,
  } = useShippingZoneOptions();
  const { regions, loadingRegions, fetchRegions } = useRegions();
  const navigate = useNavigate();

  const [isZoneModalOpen, setIsZoneModalOpen] = useState(false);
  const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalError, setModalError] = useState(null);

  const handleEditZone = async (formData) => {
    setIsSubmitting(true);
    setModalError(null);
    try {
      await updateZone(zone.id, formData);
      setIsZoneModalOpen(false);
    } catch (err) {
      setModalError(err.message || "Failed to update zone");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenCreateAssignment = () => {
    setEditingAssignment(null);
    setModalError(null);
    setIsAssignmentModalOpen(true);
  };

  const handleOpenEditAssignment = (assignment) => {
    setEditingAssignment(assignment);
    setModalError(null);
    setIsAssignmentModalOpen(true);
  };

  const handleSaveAssignment = async (formData) => {
    setIsSubmitting(true);
    setModalError(null);
    try {
      if (editingAssignment) {
        await updateShippingZoneAssignment(editingAssignment.id, formData);
      } else {
        await createShippingZoneAssignment(formData);
      }
      setIsAssignmentModalOpen(false);
    } catch (err) {
      setModalError(err.message || "Failed to save assignment");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (window.confirm("Estas seguro que quieres eliminar esta asignación?")) {
      try {
        await deleteShippingZoneAssignment(assignmentId);
      } catch (err) {
        alert("Error al eliminar la asignación");
      }
    }
  };

  if (loading || loadingOptions) {
    return <LoadingComponent />;
  }

  return (
    <>
      {error ? (
        <Alert sx={{ borderRadius: "0px !important" }} severity="error">
          {error}
        </Alert>
      ) : null}

      <Box sx={{ p: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Volver a Zonas
        </Button>
        <Button
          startIcon={<EditIcon />}
          onClick={() => setIsZoneModalOpen(true)}
          sx={{ mb: 2, ml: 2 }}
        >
          Editar Zona
        </Button>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: "100%" }}>
              <Typography variant="h4" gutterBottom>
                {zone.name}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="Courier"
                    secondary={zone.courier || "-"}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Precio"
                    secondary={`$${zone.price || 0}`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Admin"
                    secondary={zone.admin_email || "Global"}
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} md={12} className="custom-table-container">
            <Paper sx={{ p: 3, mt: 3 }}>
              <Typography
                variant="h4"
                gutterBottom
                sx={{ mb: "1.5rem !important" }}
              >
                Asignaciones a Bodegas ({assignments.length})
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenCreateAssignment}
              >
                Asignar Bodega
              </Button>
              <TableContainer>
                <Table
                  aria-label="tabla de Asignación de Zonas de despacho"
                  className="fixed-table"
                >
                  <TableHead sx={{ backgroundColor: "#fff" }}>
                    <TableRow>
                      <TableCell>Bodega</TableCell>
                      <TableCell>Cliente</TableCell>
                      <TableCell>Método de Envío</TableCell>
                      <TableCell align="right">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {assignments.map((assignment) => (
                      <TableRow
                        key={assignment.id}
                        hover
                        sx={{
                          "& > *": { borderBottom: "unset" },
                          backgroundColor: "#fff",
                        }}
                      >
                        <TableCell component="th" scope="row">
                          {assignment.warehouse_name}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {assignment.company_name}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {assignment.shipping_method}
                        </TableCell>
                        <TableCell component="th" scope="row" align="right">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEditAssignment(assignment)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleDeleteAssignment(assignment.id)
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {assignments.length === 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ p: 2, textAlign: "center" }}
                >
                  Esta zona de despacho no está asignada a ninguna bodega.
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={12} md={12} className="custom-table-container">
            <Paper sx={{ p: 3, height: "100%", mt: 2 }}>
              <ShippingZoneCommunes zoneId={zone.id} countryCode={zone.country} />
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <EditShippingZoneModal
        open={isZoneModalOpen}
        onClose={() => setIsZoneModalOpen(false)}
        onConfirm={handleEditZone}
        initialData={zone}
        communeOptions={communeOptions}
        courierOptions={courierOptions}
        adminOptions={adminOptions}
        regions={regions}
        loadingRegions={loadingRegions}
        fetchRegions={fetchRegions}
      />

      <WarehouseAssignmentModal
        open={isAssignmentModalOpen}
        onClose={() => setIsAssignmentModalOpen(false)}
        onSubmit={handleSaveAssignment}
        loading={isSubmitting}
        initialData={editingAssignment}
        apiError={modalError}
        warehouseOptions={warehouseOptions}
        methodOptions={methodOptions}
      />
    </>
  );
};

export default memo(ShippingZoneDetail);
